/**
 * ShopGB password page
 * ====================
 *
 * Modals, consent, and the analytics funnel. No dependencies.
 *
 * Consent notes, because this is the part that matters legally:
 *
 * - Google Consent Mode is set to denied in the page head, before gtag.js is
 *   asked to do anything. Nothing is stored on a device until someone agrees.
 * - Declining clears the Google Analytics cookies rather than only stopping
 *   new ones, because withdrawing has to be as easy as agreeing.
 * - Accept and Decline are the same size and weight in the CSS. Do not make
 *   Decline quieter.
 */
(function () {
	'use strict';

	var cfg = window.SGBConsent || {};
	var CONSENT_COOKIE = 'sgb_consent';
	var openModal = null;
	var lastFocused = null;
	var leadFired = false;
	var formStarted = false;

	var FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

	document.addEventListener('DOMContentLoaded', init);

	function init() {
		bindModals();
		bindConsent();
		watchRegistrationForm();
	}

	/* ------------------------------------------------------------------
	   Modals
	   ------------------------------------------------------------------ */

	function bindModals() {
		bindOpener('[data-sgb-register]', 'sgb-modal', function () {
			track('sgb_form_open', { opened_from: 'hero' });
		});

		bindOpener('[data-sgb-policy-open]', 'sgb-policy-modal', null);
		bindOpener('[data-sgb-password-open]', 'sgb-password-modal', null);

		document.addEventListener('click', function (e) {
			if (e.target.closest && e.target.closest('[data-sgb-close]')) {
				close();
			}
		});

		document.addEventListener('keydown', function (e) {
			if (!openModal) {
				return;
			}

			if (e.key === 'Escape') {
				close();
				return;
			}

			if (e.key === 'Tab') {
				trapFocus(e);
			}
		});
	}

	function bindOpener(selector, modalId, onOpen) {
		document.addEventListener('click', function (e) {
			var trigger = e.target.closest ? e.target.closest(selector) : null;

			if (!trigger) {
				return;
			}

			var modal = document.getElementById(modalId);

			if (!modal) {
				return;
			}

			e.preventDefault();

			// Opening the policy from inside another dialog should not leave
			// two stacked on top of each other.
			if (openModal && openModal !== modal) {
				close();
			}

			open(modal, trigger);

			if (typeof onOpen === 'function') {
				onOpen();
			}
		});
	}

	function open(modal, trigger) {
		lastFocused = trigger || document.activeElement;
		modal.hidden = false;
		document.body.classList.add('sgb-modal-open');
		openModal = modal;

		window.setTimeout(function () {
			var panel = modal.querySelector('.sgb-modal__panel');

			// Always start at the top. Without this a long dialog, such as the
			// policy, opens scrolled to wherever focus landed.
			if (panel) {
				panel.scrollTop = 0;
			}

			// Only a field is worth taking focus. Focusing the first button
			// instead would scroll a long dialog straight to its footer.
			var field = modal.querySelector('input:not([type="hidden"]):not([disabled]), textarea, select');

			if (field) {
				field.focus();
			} else if (panel) {
				panel.focus();
			}
		}, 40);
	}

	function close() {
		if (!openModal) {
			return;
		}

		openModal.hidden = true;
		openModal = null;
		document.body.classList.remove('sgb-modal-open');

		if (lastFocused && typeof lastFocused.focus === 'function') {
			lastFocused.focus();
		}
	}

	function trapFocus(e) {
		var panel = openModal.querySelector('.sgb-modal__panel');

		if (!panel) {
			return;
		}

		var items = Array.prototype.filter.call(
			panel.querySelectorAll(FOCUSABLE),
			function (el) { return el.offsetParent !== null; }
		);

		if (!items.length) {
			return;
		}

		var first = items[0];
		var last = items[items.length - 1];

		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	}

	/* ------------------------------------------------------------------
	   Cookies
	   ------------------------------------------------------------------ */

	function readCookie(name) {
		var parts = document.cookie ? document.cookie.split(';') : [];

		for (var i = 0; i < parts.length; i++) {
			var pair = parts[i].trim();

			if (pair.indexOf(name + '=') === 0) {
				return decodeURIComponent(pair.slice(name.length + 1));
			}
		}

		return null;
	}

	function writeCookie(name, value, days) {
		var expires = new Date(Date.now() + (days * 864e5)).toUTCString();
		var secure = window.location.protocol === 'https:' ? '; Secure' : '';

		document.cookie = name + '=' + encodeURIComponent(value) +
			'; expires=' + expires + '; path=/; SameSite=Lax' + secure;
	}

	// Cookie names to remove when analytics consent is absent.
	var ANALYTICS_PREFIXES = ['_ga', '_gid', '_gat', '_gac_'];

	function cookieDomains() {
		var host = window.location.hostname;
		var domains = [null, host, '.' + host];
		var parts = host.split('.');

		// Walk up to the registrable domain. A browser rejects anything too
		// broad, so an attempt on a public suffix simply does nothing.
		while (parts.length > 2) {
			parts.shift();
			domains.push(parts.join('.'));
			domains.push('.' + parts.join('.'));
		}

		return domains;
	}

	function deleteCookie(name) {
		var expired = '; expires=Thu, 01 Jan 1970 00:00:01 GMT';
		var domains = cookieDomains();

		for (var d = 0; d < domains.length; d++) {
			var base = encodeURIComponent(name) + '=' + expired + '; path=/';

			if (domains[d]) {
				base += '; domain=' + domains[d];
			}

			try {
				document.cookie = base;

				if (window.location.protocol === 'https:') {
					document.cookie = base + '; SameSite=Lax';
					document.cookie = base + '; SameSite=None; Secure';
				}
			} catch (err) {
				// One failed attempt is not worth stopping for.
			}
		}
	}

	function purgeAnalyticsCookies() {
		var jar = document.cookie ? document.cookie.split(';') : [];

		for (var i = 0; i < jar.length; i++) {
			var name = jar[i].split('=')[0].trim();

			if (!name) {
				continue;
			}

			for (var p = 0; p < ANALYTICS_PREFIXES.length; p++) {
				if (name.indexOf(ANALYTICS_PREFIXES[p]) === 0) {
					deleteCookie(name);
					break;
				}
			}
		}

		if (cfg.measurementId) {
			deleteCookie('_ga');
			deleteCookie('_ga_' + String(cfg.measurementId).replace(/^G-/, ''));
		}
	}

	/* ------------------------------------------------------------------
	   Consent
	   ------------------------------------------------------------------ */

	function updateConsent(granted) {
		if (typeof window.gtag === 'function') {
			window.gtag('consent', 'update', {
				'analytics_storage':  granted ? 'granted' : 'denied',
				'ad_storage':         'denied',
				'ad_user_data':       'denied',
				'ad_personalization': 'denied'
			});
		}

		// If Shopify's Customer Privacy API happens to be present, record the
		// decision there too so it carries to the live store later. Guarded,
		// because it is not available on the password page in every setup.
		try {
			if (window.Shopify && window.Shopify.customerPrivacy &&
				typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function') {
				window.Shopify.customerPrivacy.setTrackingConsent({
					analytics: granted,
					marketing: false,
					preferences: granted,
					sale_of_data: false
				}, function () {});
			}
		} catch (err) {
			// Never let this break the banner.
		}

		if (!granted) {
			purgeAnalyticsCookies();
			window.setTimeout(purgeAnalyticsCookies, 400);
			window.setTimeout(purgeAnalyticsCookies, 1500);
		}
	}

	// The banner is fixed to the bottom, so on a short page it would sit on
	// top of the legal bar and cover the policy link. Reserve exactly the
	// space it occupies, measured rather than guessed, so it still works if
	// the message is edited to be longer.
	function syncBannerSpace() {
		var banner = document.getElementById('sgb-consent');
		var space = 0;

		if (banner && !banner.hidden) {
			space = Math.ceil(banner.getBoundingClientRect().height) + 24;
		}

		document.documentElement.style.setProperty('--sgb-consent-space', space + 'px');
	}

	function bindConsent() {
		var banner = document.getElementById('sgb-consent');
		var stored = readCookie(CONSENT_COOKIE);

		window.addEventListener('resize', syncBannerSpace);

		document.addEventListener('click', function (e) {
			var btn = e.target.closest ? e.target.closest('[data-sgb-consent]') : null;

			if (btn) {
				e.preventDefault();
				decide(btn.getAttribute('data-sgb-consent') === 'accept');
				return;
			}

			// Reopening the banner from the policy or the footer link.
			var reopen = e.target.closest ? e.target.closest('[data-sgb-consent-open]') : null;

			if (reopen && banner) {
				e.preventDefault();

				if (openModal) {
					close();
				}

				banner.hidden = false;
				syncBannerSpace();
			}
		});

		if (!banner) {
			// Banner switched off in the theme editor. Honour a previous
			// acceptance, otherwise stay denied.
			if (stored === 'accept') {
				updateConsent(true);
			} else {
				updateConsent(false);
			}
			return;
		}

		if (stored === 'accept') {
			updateConsent(true);
		} else if (stored === 'deny') {
			updateConsent(false);
		} else {
			updateConsent(false);
			banner.hidden = false;
		}

		syncBannerSpace();
	}

	function decide(granted) {
		var banner = document.getElementById('sgb-consent');

		writeCookie(CONSENT_COOKIE, granted ? 'accept' : 'deny', cfg.cookieDays || 365);
		updateConsent(granted);

		if (banner) {
			banner.hidden = true;
		}

		syncBannerSpace();
	}

	/* ------------------------------------------------------------------
	   Analytics funnel

	   Three events: sgb_form_open, sgb_form_start, generate_lead.

	   NO PERSONAL DATA IS SENT. Not the name, email, phone or website.

	   One honest caveat versus the WordPress build. There, generate_lead
	   fired on a server-confirmed success, so spam and validation failures
	   never counted. Shopify Forms owns its own markup and submission, so
	   here we watch for the form being replaced by its success state. If that
	   detection ever stops matching a Forms update, you still have
	   sgb_form_submit, which counts attempts.
	   ------------------------------------------------------------------ */

	function track(eventName, params) {
		var payload = params || {};

		try {
			if (typeof window.gtag === 'function') {
				window.gtag('event', eventName, payload);
			}

			if (Array.isArray(window.dataLayer)) {
				var entry = { event: eventName };

				for (var key in payload) {
					if (Object.prototype.hasOwnProperty.call(payload, key)) {
						entry[key] = payload[key];
					}
				}

				window.dataLayer.push(entry);
			}
		} catch (err) {
			// Analytics must never be able to break the page.
		}
	}

	function watchRegistrationForm() {
		var host = document.querySelector('.sgb-modal__form, .sgb-inlineform');

		if (!host) {
			return;
		}

		// First keystroke anywhere in the form.
		host.addEventListener('input', function () {
			if (!formStarted) {
				formStarted = true;
				track('sgb_form_start');
			}
		}, true);

		host.addEventListener('submit', function () {
			track('sgb_form_submit');
		}, true);

		if (typeof window.MutationObserver !== 'function') {
			return;
		}

		// Shopify Forms swaps the form out for a confirmation once it has
		// accepted a submission. That swap is the success signal.
		var observer = new MutationObserver(function () {
			if (leadFired || !formStarted) {
				return;
			}

			if (!host.querySelector('form') && host.textContent.trim() !== '') {
				leadFired = true;
				track('generate_lead', { method: 'seller_registration_form' });
			}
		});

		observer.observe(host, { childList: true, subtree: true });
	}
})();

/* --------------------------------------------------------------------------
   Shopify Forms label fix

   The app renders inside a shadow root, so page CSS cannot reach it. Its own
   stylesheet moves the label with `transform: var(--input-label-transform)`,
   but that custom property is never defined, so the rule is invalid and the
   label never lifts. Their script also sets `top: 23.5px` inline on each
   label, centring it, which is what the typed text then collides with.

   Rather than chase their filled and focus states (both depend on the same
   broken variable, so the label jumps as you tab between fields), the label is
   parked in its lifted position in every state: no transform at all, just a
   fixed top and the smaller type size their lifted label would have had. The
   label never moves, and the value clears it because the input's own
   padding-top (22.5px) already leaves the top of the field empty.

   Matched on a partial class name because the app hashes class names per
   build. The numbers suit their 47px single-line fields; a multi-line field
   would need its own rule. If Shopify defines the variable in a later
   release, this can be deleted.
   -------------------------------------------------------------------------- */
(function () {
	var CSS =
		'label[class*="_formInputFieldLabel"]{top:7px!important;transform:none!important;font-size:12px!important;line-height:12px!important;}';
	var STYLE_ID = 'sgb-forms-label-fix';
	var tries = 0;

	function patch() {
		var host = document.querySelector('shopify-forms-embed');
		var root = host && host.shadowRoot;

		if (root && !root.getElementById(STYLE_ID)) {
			var style = document.createElement('style');
			style.id = STYLE_ID;
			style.textContent = CSS;
			root.appendChild(style);
		}

		/* The embed renders late and re-renders after a submission, so keep
		   looking for a while rather than giving up on the first pass. */
		tries += 1;
		if (tries < 40) {
			setTimeout(patch, 500);
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', patch);
	} else {
		patch();
	}
})();
